import { __awaiter } from "tslib";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OrderDocumentType, useCheckinSaveOrderServicesMutation, useOrderDocumentQuery, useSaveOrderServicesMutation } from '@websky/graphql';
import { getAvailableInsuranceCodesByPrograms, getAvailableSelectedInsuranceCodes, getImportantInsuranceCodes as getCheckoutImportantInsuranceCodes, getOrder as getCheckoutOrder, getOrderId as getCheckoutOrderId } from '../Checkout/store/order/selectors';
import { getInsurancePriceToPay as getCheckoutInsurancePriceToPay, getTotalPriceToPay as getCheckoutTotalPriceToPay } from '../Checkout/store/orderPrice/selectors';
import { fillOrder } from '../Checkout/store/order/actions';
import { Mode } from '../types';
import { Currency } from '../enums';
import { getCheckinOrder, getImportantInsuranceCodes as getCheckinImportantInsuranceCodes, getInsurancePrice as getCheckinInsurancePriceToPay, getTotalPriceToPay as getCheckinTotalPriceToPay, getCheckinOrderId } from '../Modules/Checkin/store/order/selectors';
import { fillCheckinOrder } from '../Modules/Checkin/store/order/actions';
import { getSelectedInsuranceSaveCodes } from './utils';
import { saveOrderServicesParamsFactory } from './store/utils';
import { useInsuranceUpsaleModal } from './components/InsuranceUpsaleModal/InsuranceUpsaleModal.hook';
import { ModeContext, useConfig } from '../context';
import { get, IMPORTANT_INSURANCE_TOUCHED, sessionStorageGet, sessionStorageSet, set } from '../cache';
import { useToggleable } from '../hooks';
import { downloadPDF } from '../utils';
export const useInsurancePageWrapper = ({ strategy, insurancePrograms }) => {
    var _a;
    const dispatch = useDispatch();
    const mode = useContext(ModeContext);
    const order = mode === Mode.Checkin ? useSelector(getCheckinOrder) : useSelector(getCheckoutOrder);
    const orderPriceToPay = mode === Mode.Checkin ? useSelector(getCheckinTotalPriceToPay) : useSelector(getCheckoutTotalPriceToPay);
    const orderInsuranceToPay = mode === Mode.Checkin
        ? useSelector(getCheckinInsurancePriceToPay)
        : useSelector(getCheckoutInsurancePriceToPay);
    // доступные коды страховок
    const availableInsuranceCodes = useSelector(getAvailableInsuranceCodesByPrograms(insurancePrograms));
    // выбранные коды страховок
    const selectedInsuranceCodes = useSelector(getAvailableSelectedInsuranceCodes(availableInsuranceCodes));
    // другие выбранные коды страховок (вне группы)
    const otherSelectedInsuranceCodes = useSelector(getAvailableSelectedInsuranceCodes(availableInsuranceCodes, false));
    const importantInsurances = useImportantInsurances({
        availableInsuranceCodes,
        selectedInsuranceCodes
    });
    const { global: { insurance: { allowImplicitConfirm } } } = useConfig();
    const [isNeedToSendSaveRequest, setIsNeedToSendSaveReq] = useState(false);
    const { isOpen: isShowImportantInsurancesError, open: onShowImportantInsurancesError, close: onHideImportantInsurancesError } = useToggleable(false);
    const [saveOrderServices, mutationResult] = mode === Mode.Checkin ? useCheckinSaveOrderServicesMutation() : useSaveOrderServicesMutation();
    const { refetch: getDocument } = useOrderDocumentQuery({ skip: true });
    const [saveOrderServicesParams, setSaveOrderServicesParams] = useState();
    const selectedSaveServicesCodes = useMemo(() => {
        var _a;
        return getSelectedInsuranceSaveCodes((_a = saveOrderServicesParams === null || saveOrderServicesParams === void 0 ? void 0 : saveOrderServicesParams.params) === null || _a === void 0 ? void 0 : _a.insurances, availableInsuranceCodes);
    }, [(_a = saveOrderServicesParams === null || saveOrderServicesParams === void 0 ? void 0 : saveOrderServicesParams.params) === null || _a === void 0 ? void 0 : _a.insurances, availableInsuranceCodes]);
    const insuranceCodesToSave = useMemo(() => {
        return [...new Set([...otherSelectedInsuranceCodes, ...selectedSaveServicesCodes])];
    }, [otherSelectedInsuranceCodes, selectedSaveServicesCodes]);
    const insuranceUpsaleModal = useInsuranceUpsaleModal({
        strategy,
        availableInsuranceCodes,
        selectedInsuranceCodes: insuranceCodesToSave
    });
    useEffect(() => {
        var _a, _b;
        if (mode === Mode.Checkin) {
            if ((_a = mutationResult === null || mutationResult === void 0 ? void 0 : mutationResult.data) === null || _a === void 0 ? void 0 : _a.CheckinSaveOrderServices) {
                dispatch(fillCheckinOrder(mutationResult.data.CheckinSaveOrderServices));
            }
            return;
        }
        if ((_b = mutationResult === null || mutationResult === void 0 ? void 0 : mutationResult.data) === null || _b === void 0 ? void 0 : _b.SaveOrderServices) {
            dispatch(fillOrder(mutationResult.data.SaveOrderServices));
        }
    }, [mutationResult === null || mutationResult === void 0 ? void 0 : mutationResult.data]);
    const [insurancePriceToPay, setInsurancePriceToPay] = useState({
        amount: 0,
        currency: Currency.RUB
    });
    const priceWithoutInsurance = Object.assign(Object.assign({}, orderPriceToPay), { amount: orderPriceToPay.amount - orderInsuranceToPay.amount });
    const totalPriceToPay = Object.assign(Object.assign({}, priceWithoutInsurance), { amount: priceWithoutInsurance.amount + insurancePriceToPay.amount });
    const isNeedSelectImportantInsurances = useMemo(() => {
        if (allowImplicitConfirm) {
            return false;
        }
        return !importantInsurances.isAllImportantInsurancesTouchedOrSelected;
    }, [allowImplicitConfirm, importantInsurances.isAllImportantInsurancesTouchedOrSelected]);
    const saveOrderServicesWithParams = useCallback((codes) => {
        var _a, _b;
        const availableTravellersIdSet = new Set();
        (_b = (_a = order.additionalServices.insurances) === null || _a === void 0 ? void 0 : _a.insurancePrograms) === null || _b === void 0 ? void 0 : _b.forEach(insurance => {
            var _a;
            if (codes.includes(insurance.code)) {
                (_a = insurance.allowedPassengers) === null || _a === void 0 ? void 0 : _a.forEach(id => availableTravellersIdSet.add(id.toString()));
            }
        });
        const travellerIds = order.travellers
            .filter(traveller => availableTravellersIdSet.has(traveller.id))
            .map(traveller => traveller.id);
        const saveOrderParams = saveOrderServicesParamsFactory(travellerIds, codes);
        return saveOrderServices({
            variables: {
                params: Object.assign(Object.assign({}, saveOrderParams.params), { id: order.id })
            }
        });
    }, [order.travellers, order.additionalServices.insurances, saveOrderServices]);
    const saveOrderServicesRequest = useCallback((codes = []) => {
        const selectedCodesSet = new Set([...codes, ...insuranceCodesToSave]);
        return saveOrderServicesWithParams([...selectedCodesSet]);
    }, [insuranceCodesToSave, saveOrderServicesWithParams]);
    const handleAgree = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        yield insuranceUpsaleModal.onAgree((insuranceCodes) => __awaiter(void 0, void 0, void 0, function* () {
            if (!(insuranceCodes === null || insuranceCodes === void 0 ? void 0 : insuranceCodes.length)) {
                return;
            }
            yield saveOrderServicesRequest(insuranceCodes);
        }));
    }), [saveOrderServicesRequest, insuranceUpsaleModal.onAgree]);
    const handleDisagree = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        yield insuranceUpsaleModal.onDisagree(() => __awaiter(void 0, void 0, void 0, function* () {
            if (!isNeedToSendSaveRequest) {
                return;
            }
            yield saveOrderServicesRequest();
        }));
    }), [isNeedToSendSaveRequest, saveOrderServicesRequest, insuranceUpsaleModal.onDisagree]);
    const saveOrderServicesCallback = useCallback((callback) => __awaiter(void 0, void 0, void 0, function* () {
        if (isNeedSelectImportantInsurances) {
            onShowImportantInsurancesError();
            return;
        }
        yield insuranceUpsaleModal.onContinue(() => __awaiter(void 0, void 0, void 0, function* () {
            if (isNeedToSendSaveRequest) {
                yield saveOrderServicesRequest();
            }
            callback();
        }));
    }), [
        isNeedToSendSaveRequest,
        saveOrderServicesRequest,
        isNeedSelectImportantInsurances,
        insuranceUpsaleModal.onContinue
    ]);
    const onDownloadNasInsurance = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const { data } = yield getDocument({
            params: { orderId: order.id, documentType: OrderDocumentType.NASTicket }
        });
        if (data === null || data === void 0 ? void 0 : data.OrderDocument) {
            downloadPDF(data.OrderDocument, 'insurance.pdf');
        }
    }), [order.id]);
    return {
        // methods
        saveOrderServices,
        saveOrderServicesRequest,
        saveOrderServicesWithParams,
        saveOrderServicesCallback,
        setInsurancePriceToPay,
        setIsNeedToSendSaveReq,
        setSaveOrderServicesParams,
        onHideImportantInsurancesError,
        closeUpsaleModal: insuranceUpsaleModal.onCloseUpsaleModal,
        onContinue: insuranceUpsaleModal.onContinue,
        handleAgree,
        handleDisagree,
        onDownloadNasInsurance,
        // data
        order,
        orderId: order.id,
        orderPriceToPay,
        insurancePriceToPay,
        totalPriceToPay,
        mutationResult,
        saveOrderServicesParams,
        selectedSaveServicesCodes,
        priorityInsurance: insuranceUpsaleModal.priorityInsurance,
        isNeedToSendSaveRequest,
        isUpsaleOpen: insuranceUpsaleModal.isUpsaleOpen,
        isLoading: mutationResult.loading,
        isShowImportantInsurancesError
    };
};
function getStrategyKey(strategy, orderId) {
    return `${strategy}_${orderId}`;
}
export const useInsuranceUpsaleConfig = () => {
    const updateId = useRef(0);
    const mode = useContext(ModeContext);
    const order = mode === Mode.Checkin ? useSelector(getCheckinOrder) : useSelector(getCheckoutOrder);
    const { global: { insurance: { upsaleStrategy } } } = useConfig();
    const [shownStrategies, unShownStrategies] = useMemo(() => {
        const shown = [];
        const unShown = [];
        upsaleStrategy.forEach(strategyKey => {
            const existKey = get(`${strategyKey}_${order.id}`);
            if (existKey) {
                shown.push(strategyKey);
            }
            else {
                unShown.push(strategyKey);
            }
        });
        return [shown, unShown];
    }, [upsaleStrategy, order.id, updateId.current]);
    const getIsNeedToShow = useCallback((strategy) => {
        return upsaleStrategy.includes(strategy) && unShownStrategies.includes(strategy);
    }, [upsaleStrategy, unShownStrategies]);
    const onApplyStrategy = useCallback((strategy) => {
        set(getStrategyKey(strategy, order.id), true);
        updateId.current++;
    }, [order.id, updateId.current]);
    const onCancelStrategy = useCallback((strategy) => {
        set(getStrategyKey(strategy, order.id), false);
        updateId.current++;
    }, [order.id, updateId.current]);
    return {
        // data
        upsaleStrategy,
        shownStrategies,
        unShownStrategies,
        // methods
        getIsNeedToShow,
        onApplyStrategy,
        onCancelStrategy
    };
};
export const useImportantInsurances = ({ availableInsuranceCodes = [], selectedInsuranceCodes = [] } = {}) => {
    var _a;
    const mode = useContext(ModeContext);
    const orderId = mode === Mode.Checkin ? useSelector(getCheckinOrderId) : useSelector(getCheckoutOrderId);
    const importantInsuranceCodes = mode === Mode.Checkin
        ? useSelector(getCheckinImportantInsuranceCodes(availableInsuranceCodes))
        : useSelector(getCheckoutImportantInsuranceCodes(availableInsuranceCodes));
    const importantInsurancesTouchedKey = `${IMPORTANT_INSURANCE_TOUCHED}_${orderId}`;
    const importantInsurancesTouchedCodes = (_a = sessionStorageGet(importantInsurancesTouchedKey)) !== null && _a !== void 0 ? _a : [];
    const isAllImportantInsurancesTouchedOrSelected = useMemo(() => {
        if (!importantInsuranceCodes.length) {
            return true;
        }
        return importantInsuranceCodes.every(code => importantInsurancesTouchedCodes.includes(code) || selectedInsuranceCodes.includes(code));
    }, [importantInsuranceCodes, importantInsurancesTouchedCodes, selectedInsuranceCodes]);
    // Save in the session the parameter of the touched important
    // insurance to display pressed radio buttons in the SelectForm
    const onTouchInsurance = useCallback((code) => {
        if (importantInsuranceCodes.includes(code)) {
            const codes = [...new Set([...importantInsurancesTouchedCodes, code])];
            sessionStorageSet(importantInsurancesTouchedKey, codes);
        }
    }, [orderId, importantInsuranceCodes, importantInsurancesTouchedCodes]);
    const getIsTouched = useCallback((code) => importantInsurancesTouchedCodes.includes(code), [
        importantInsurancesTouchedCodes
    ]);
    return {
        // data
        importantInsuranceCodes,
        importantInsurancesTouchedCodes,
        isAllImportantInsurancesTouchedOrSelected,
        // methods
        onTouchInsurance,
        getIsTouched
    };
};
export const useInsuranceRoute = ({ goToNextStep, strategy }) => {
    const insurancePageWrapper = useInsurancePageWrapper({ strategy });
    const handleNextStep = () => __awaiter(void 0, void 0, void 0, function* () {
        yield insurancePageWrapper.saveOrderServicesCallback(goToNextStep);
    });
    const handleAgree = () => __awaiter(void 0, void 0, void 0, function* () {
        if (insurancePageWrapper.priorityInsurance) {
            yield insurancePageWrapper.handleAgree();
            goToNextStep();
            return;
        }
        insurancePageWrapper.handleAgree();
    });
    const handleDisagree = () => __awaiter(void 0, void 0, void 0, function* () {
        yield insurancePageWrapper.handleDisagree();
        goToNextStep();
    });
    return {
        // methods
        handleNextStep,
        handleAgree,
        handleDisagree,
        setInsurancePriceToPay: insurancePageWrapper.setInsurancePriceToPay,
        setSaveOrderServicesParams: insurancePageWrapper.setSaveOrderServicesParams,
        setIsNeedToSendSaveReq: insurancePageWrapper.setIsNeedToSendSaveReq,
        onDownloadNasInsurance: insurancePageWrapper.onDownloadNasInsurance,
        // data
        insurancePriceToPay: insurancePageWrapper.insurancePriceToPay,
        orderPriceToPay: insurancePageWrapper.orderPriceToPay,
        totalPriceToPay: insurancePageWrapper.totalPriceToPay,
        priorityInsurance: insurancePageWrapper.priorityInsurance,
        isUpsaleOpen: insurancePageWrapper.isUpsaleOpen,
        isLoading: insurancePageWrapper.isLoading,
        isShowImportantInsurancesError: insurancePageWrapper.isShowImportantInsurancesError
    };
};
